import { buildFullAddress } from '@client/core/address/build-full-address';
import { fromJson } from '@client/core/data/concerns/parses-from-json';
import { defineTransform } from '@client/core/data/concerns/parses-from-json/transform-map';
import { Nullable } from '@client/core/data/types';
import { USStates } from '@client/features/location/types';
import { InvestmentPacketFields } from '@client/features/project/models/investment-packet-fields';
import {
  ProjectActiveStatus,
  ProjectInvestmentStrategy,
  ProjectLeadSource,
  ProjectStage,
} from '@client/features/project/types';
import { parseISO } from '@client/core/utils/date';
import { toEnum } from '@client/core/data/concerns/parses-from-json/to-enum';
import { PropertyType } from '@client/features/project/models/property-type';
import { Address } from '@client/core/address/types';

/**
 * @see app/Http/Resources/Project/ProjectResource.php
 */
export class Project {
  constructor(
    public readonly id: number,
    public readonly uuid: string,
    public readonly createdAt: Nullable<Date>,
    public readonly updatedAt: Nullable<Date>,
    public readonly stage: ProjectStage,
    public readonly stageGroupText: string,
    public readonly stageText: string,
    public readonly leadSource: null | ProjectLeadSource,
    public readonly investmentStrategy: null | ProjectInvestmentStrategy,
    public readonly constructionStrategyText: string,
    public readonly address1: string,
    public readonly address2: Nullable<string>,
    public readonly city: Nullable<string>,
    // TODO: This enum isn't necessarily true. Users can input any text here, not just two-character abbreviation.
    public readonly region: Nullable<USStates>,
    public readonly postalCode: Nullable<string>,
    public readonly country: Nullable<string>,
    public readonly name: string,
    public readonly neighborhood: Nullable<string>,
    public readonly parking: Nullable<string>,
    public readonly status: ProjectActiveStatus,
    public readonly type: Nullable<string>,
    public readonly style: Nullable<string>,
    public readonly yearBuilt: Nullable<number>,
    public readonly beds: Nullable<number>,
    // Full Baths
    public readonly baths: Nullable<number>,
    public readonly halfBaths: Nullable<number>,
    public readonly garages: Nullable<number>,
    public readonly squareFeet: Nullable<number>,
    /** @deprecated Use ProjectFlipAnalysis */
    public readonly afterRepairValue: Nullable<number>,
    /** @deprecated Use ProjectFlipAnalysis */
    public readonly purchasePrice: Nullable<number>,
    public readonly latitude: Nullable<number>,
    public readonly longitude: Nullable<number>,
    public readonly investmentPacket: Nullable<InvestmentPacketFields>,
    public readonly featuredImageUrl: string,
    // Hex color such as "#9CCBFA"
    public readonly color: string,
  ) {}

  get fullAddress(): string {
    const address: Address = {
      address1: this.address1,
      address2: this.address2,
      city: this.city,
      postalCode: this.postalCode,
      region: this.region,
    };

    return buildFullAddress(address);
  }

  get constructionTypeText(): string {
    const text = this.type ? PropertyType.findBySlug(this.type) : null;

    return text?.name || this.type || '';
  }
}

defineTransform(Project, (json, { fromSnakeCaseKeys, nullOr }) => {
  return {
    ...fromSnakeCaseKeys(),
    createdAt: json.created_at ? parseISO(json.created_at) : null,
    updatedAt: json.updated_at ? parseISO(json.updated_at) : null,
    stage: json.stage ? toEnum(ProjectStage, json.stage) : null,
    address1: json.address_1,
    address2: json.address_2,
    latitude: nullOr(json.lat, () => parseFloat(json.lat)),
    longitude: nullOr(json.lng, () => parseFloat(json.lng)),
    halfBaths: nullOr(json.half_bath_count, () => parseInt(json.half_bath_count, 10)),
    investmentPacket: json.investment_packet ? fromJson(InvestmentPacketFields, json.investment_packet) : null,
    leadSource: json.lead_source ? toEnum(ProjectLeadSource, json.lead_source) : null,
    investmentStrategy: json.investment_strategy ? toEnum(ProjectInvestmentStrategy, json.investment_strategy) : null,
  };
});
